import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBookmarks } from '../hooks/useBookmarks';
import { useNotes } from '../hooks/useNotes';

const BookmarksNotesPanel = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('bookmarks');
  const navigate = useNavigate();
  
  const { bookmarks, removeBookmark, getGroupedBookmarks } = useBookmarks();
  const { notes, getGroupedNotes } = useNotes();
  
  const groupedBookmarks = getGroupedBookmarks();
  const groupedNotes = getGroupedNotes();

  const handleNavigate = (surahNumber, ayahNumber = null) => {
    navigate(`/surah/${surahNumber}${ayahNumber ? `?ayah=${ayahNumber}&mode=scroll` : ''}`);
    onClose();
  };

  const formatTimestamp = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    return `${Math.floor(days / 30)} months ago`;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[60]"
        style={{
          background: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(2px)',
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div 
        className="fixed right-0 top-0 bottom-0 w-full md:w-96 z-[61] overflow-y-auto"
        style={{
          background: 'var(--bg-overlay)',
          backdropFilter: 'blur(16px)',
          borderLeft: '2px solid var(--gold-primary)',
          boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.3)',
          animation: 'slide-in-right 300ms ease-out',
        }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 z-10 p-6 border-b"
          style={{
            background: 'var(--bg-secondary)',
            borderColor: 'var(--border-default)',
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 
              className="text-2xl font-bold"
              style={{ color: 'var(--text-arabic)' }}
            >
              Saved
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
              style={{
                color: 'var(--text-secondary)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div 
            className="flex gap-2 p-1 rounded-lg"
            style={{
              background: 'rgba(148, 163, 184, 0.08)',
            }}
          >
            <button
              onClick={() => setActiveTab('bookmarks')}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: activeTab === 'bookmarks' ? 'var(--gold-muted)' : 'transparent',
                border: activeTab === 'bookmarks' ? '1px solid var(--border-gold)' : '1px solid transparent',
                color: activeTab === 'bookmarks' ? 'var(--gold-hover)' : 'var(--text-secondary)',
              }}
            >
              📚 Saved ({bookmarks.length})
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: activeTab === 'notes' ? 'var(--gold-muted)' : 'transparent',
                border: activeTab === 'notes' ? '1px solid var(--border-gold)' : '1px solid transparent',
                color: activeTab === 'notes' ? 'var(--gold-hover)' : 'var(--text-secondary)',
              }}
            >
              📝 Reflections ({notes.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'bookmarks' && (
            <>
              {bookmarks.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-5xl mb-4">📖</div>
                  <p 
                    className="text-lg font-medium mb-2"
                    style={{ color: 'var(--text-english)' }}
                  >
                    No saved reflections yet
                  </p>
                  <p 
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)', opacity: 0.7 }}
                  >
                    Bookmark Surahs or Ayahs to return later
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedBookmarks).map(([surahNumber, data]) => (
                    <div 
                      key={surahNumber}
                      className="p-4 rounded-xl"
                      style={{
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-default)',
                      }}
                    >
                      <div className="mb-3">
                        <h3 
                          className="text-arabic text-lg font-bold"
                          style={{ color: 'var(--text-arabic)' }}
                        >
                          {data.surahName}
                        </h3>
                        <p 
                          className="text-sm"
                          style={{ color: 'var(--gold-hover)' }}
                        >
                          {data.surahEnglishName}
                        </p>
                      </div>

                      {data.ayahs.length > 0 && (
                        <div className="space-y-2">
                          {data.ayahs.map((ayah) => (
                            <div
                              key={ayah.id}
                              className="group flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all"
                              style={{
                                background: 'rgba(148, 163, 184, 0.04)',
                                border: '1px solid transparent',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.08)';
                                e.currentTarget.style.borderColor = 'var(--border-gold)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(148, 163, 184, 0.04)';
                                e.currentTarget.style.borderColor = 'transparent';
                              }}
                              onClick={() => handleNavigate(ayah.surahNumber, ayah.ayahNumber)}
                            >
                              <div className="flex-1">
                                <p 
                                  className="text-xs font-semibold mb-1"
                                  style={{ color: 'var(--text-secondary)' }}
                                >
                                  Ayah {ayah.ayahNumber}
                                </p>
                                <p 
                                  className="text-sm line-clamp-2"
                                  style={{ color: 'var(--text-english)' }}
                                >
                                  {ayah.ayahText || 'Click to view'}
                                </p>
                                <p 
                                  className="text-xs mt-1"
                                  style={{ color: 'var(--text-tertiary)' }}
                                >
                                  {formatTimestamp(ayah.timestamp)}
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeBookmark(ayah.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 w-6 h-6 rounded flex items-center justify-center text-xs transition-all"
                                style={{
                                  color: 'var(--error)',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'notes' && (
            <>
              {notes.length === 0 ? (
                <div className="text-center py-16">
                  <div className="text-5xl mb-4">✍️</div>
                  <p 
                    className="text-lg font-medium mb-2"
                    style={{ color: 'var(--text-english)' }}
                  >
                    No reflections yet
                  </p>
                  <p 
                    className="text-sm"
                    style={{ color: 'var(--text-secondary)', opacity: 0.7 }}
                  >
                    Add notes to Ayahs for personal reflection
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedNotes).map(([surahNumber, data]) => (
                    <div 
                      key={surahNumber}
                      className="p-4 rounded-xl"
                      style={{
                        background: 'var(--bg-secondary)',
                        border: '1px solid var(--border-default)',
                      }}
                    >
                      <div className="mb-3">
                        <h3 
                          className="text-arabic text-lg font-bold"
                          style={{ color: 'var(--text-arabic)' }}
                        >
                          {data.surahName}
                        </h3>
                        <p 
                          className="text-sm"
                          style={{ color: 'var(--gold-hover)' }}
                        >
                          {data.surahEnglishName}
                        </p>
                      </div>

                      <div className="space-y-2">
                        {data.notes.map((note) => (
                          <div
                            key={note.id}
                            className="p-3 rounded-lg cursor-pointer transition-all"
                            style={{
                              background: 'rgba(148, 163, 184, 0.04)',
                              border: '1px solid transparent',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(212, 175, 55, 0.08)';
                              e.currentTarget.style.borderColor = 'var(--border-gold)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(148, 163, 184, 0.04)';
                              e.currentTarget.style.borderColor = 'transparent';
                            }}
                            onClick={() => handleNavigate(note.surahNumber, note.ayahNumber)}
                          >
                            <p 
                              className="text-xs font-semibold mb-2"
                              style={{ color: 'var(--text-secondary)' }}
                            >
                              Ayah {note.ayahNumber}
                            </p>
                            <p 
                              className="text-sm line-clamp-3 mb-2"
                              style={{ color: 'var(--text-english)' }}
                            >
                              {note.content}
                            </p>
                            <p 
                              className="text-xs"
                              style={{ color: 'var(--text-tertiary)' }}
                            >
                              {formatTimestamp(note.timestamp)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default BookmarksNotesPanel;

